import { RowSelectionState } from "@tanstack/react-table";
import { roundToCents } from "@common/helpers/monetary";
import { APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import { ScheduleBillingRowInfo } from "./scheduleBillingRowHelpers";
import {
    getScheduleRowApplicationId,
    getSingleScheduleEntityId,
    getUniqueScheduleApplicationIds,
} from "./scheduleApplicationRowId";
import { getAvailableScheduleLifecycleVerbs } from "./scheduleApplicationStatusDisplay";
import {
    SCHEDULE_BULK_LIFECYCLE_LIMIT_REASON,
    SCHEDULE_BULK_LIFECYCLE_SKIP_REASONS,
    SCHEDULE_BULK_NONE_ELIGIBLE_REASON,
    ScheduleBulkLifecycleVerb,
    ScheduleBulkSkippedRow,
} from "./scheduleLifecycleBulkCopy";
import { ScheduleStopSummary } from "./scheduleStopSettlementCopy";

type LifecycleRow = Pick<
    ScheduleApplicationParentRow,
    | "id"
    | "scheduleApplicationId"
    | "entityId"
    | "status"
    | "isMilestone"
    | "sourceLabel"
>;

export interface ScheduleBulkLifecyclePartition<TRow extends LifecycleRow> {
    eligible: TRow[];
    skipped: ScheduleBulkSkippedRow[];
}

type StopTargetRow = Pick<
    ScheduleApplicationParentRow,
    | "id"
    | "scheduleApplicationId"
    | "entityId"
    | "revenueSubMode"
    | "remainingAfterStopAmount"
> & { billing: Pick<ScheduleBillingRowInfo, "scheduleType"> };

export interface ScheduleBulkEntityGroup {
    entityId: number;
    applicationIds: string[];
}

export interface ScheduleStopBulkTarget {
    count: number;
    summary: ScheduleStopSummary;
    entityId: number | null;
}

export function buildScheduleStopBulkTarget(
    rows: StopTargetRow[],
): ScheduleStopBulkTarget {
    const remainingByApplication = new Map<string, number>();
    for (const row of rows) {
        remainingByApplication.set(
            getScheduleRowApplicationId(row),
            row.remainingAfterStopAmount,
        );
    }
    const revenueSubModes = new Set(rows.map((row) => row.revenueSubMode));
    const [onlyRevenueSubMode] = revenueSubModes;
    return {
        count: remainingByApplication.size,
        entityId: getSingleScheduleEntityId(rows),
        summary: {
            remainingAfterStopAmount: roundToCents(
                [...remainingByApplication.values()].reduce(
                    (sum, amount) => sum + amount,
                    0,
                ),
            ),
            recognitionAccountName: null,
            holdingAccountName: null,
            scheduleType: rows[0].billing.scheduleType,
            revenueSubMode:
                revenueSubModes.size === 1
                    ? (onlyRevenueSubMode ?? null)
                    : null,
            expenseSubMode: null,
        },
    };
}

export function groupScheduleRowsByEntity(
    rows: LifecycleRow[],
): ScheduleBulkEntityGroup[] {
    const byEntity = new Map<number, Set<string>>();
    for (const row of rows) {
        const applicationIds = byEntity.get(row.entityId) ?? new Set<string>();
        applicationIds.add(getScheduleRowApplicationId(row));
        byEntity.set(row.entityId, applicationIds);
    }
    return [...byEntity.entries()].map(([entityId, applicationIds]) => ({
        entityId,
        applicationIds: [...applicationIds],
    }));
}

export function partitionScheduleRowsForLifecycle<
    TRow extends LifecycleRow,
>(options: {
    verb: ScheduleBulkLifecycleVerb;
    rows: TRow[];
}): ScheduleBulkLifecyclePartition<TRow> {
    const { verb, rows } = options;
    const eligible: TRow[] = [];
    const skippedById = new Map<string, ScheduleBulkSkippedRow>();
    for (const row of rows) {
        if (getAvailableScheduleLifecycleVerbs(row).includes(verb)) {
            eligible.push(row);
            continue;
        }
        const applicationId = getScheduleRowApplicationId(row);
        skippedById.set(applicationId, {
            applicationId,
            label: row.sourceLabel,
            reason: SCHEDULE_BULK_LIFECYCLE_SKIP_REASONS[verb],
        });
    }
    return { eligible, skipped: [...skippedById.values()] };
}

export function getScheduleBulkLifecycleDisabledReason(options: {
    verb: ScheduleBulkLifecycleVerb;
    rows: LifecycleRow[];
}): string | undefined {
    const { verb, rows } = options;
    if (
        getUniqueScheduleApplicationIds(rows).length >
        APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT
    ) {
        return SCHEDULE_BULK_LIFECYCLE_LIMIT_REASON;
    }
    const { eligible } = partitionScheduleRowsForLifecycle({ verb, rows });
    return eligible.length === 0 && rows.length > 0
        ? SCHEDULE_BULK_NONE_ELIGIBLE_REASON
        : undefined;
}

export function buildFailedRowSelection(options: {
    rows: LifecycleRow[];
    failedApplicationIds: string[];
}): RowSelectionState {
    const failed = new Set(options.failedApplicationIds);
    return Object.fromEntries(
        options.rows
            .filter((row) => failed.has(getScheduleRowApplicationId(row)))
            .map((row) => [row.id, true]),
    );
}
